import { evo_preview_error, evo_preview_title, exit_preview } from '../translations.json';
import { removeQueryParamPreservingEncoding } from '../utils/url';

export const EVO_PREVIEW_TIMEOUT_MS = 12000;
/** Allows one late readiness signal to reconcile a timed-out preview load. */
export const EVO_PREVIEW_LATE_READY_GRACE_MS = 5000;
export const EVO_PREVIEW_SCRIPT_ID = 'guestline-evo-preview-tag';

const EVO_PREVIEW_SCRIPT_ATTRIBUTE = 'data-guestline-evo-preview';
const READY_EVENT = 'evo-ibe-tag:ready';
const ERROR_EVENT = 'evo-ibe-tag:error';

type PreviewLanguage = keyof typeof evo_preview_error;
type EvoPreviewErrorCode =
  | 'missing-script-source'
  | 'malformed-script-source'
  | 'script-append-error'
  | 'lifecycle-error'
  | 'script-load-error'
  | 'timeout';

export class EvoPreviewError extends Error {
  readonly code: EvoPreviewErrorCode;

  constructor(code: EvoPreviewErrorCode) {
    super(`EVO preview failed: ${code}`);
    this.name = 'EvoPreviewError';
    this.code = code;
  }
}

function getPreviewLanguage(legacyTag: HTMLScriptElement): PreviewLanguage {
  const candidates = [legacyTag.getAttribute('data-lng'), window.navigator.language];

  for (const candidate of candidates) {
    const language = candidate?.trim().toLowerCase().split(/[-_]/)[0];

    if (language && Object.prototype.hasOwnProperty.call(evo_preview_error, language)) {
      return language as PreviewLanguage;
    }
  }

  return 'en';
}

function getEvoPreviewScriptUrl(legacyTag: HTMLScriptElement): string {
  let legacyTagSrc: string;

  try {
    legacyTagSrc = legacyTag.src;
  } catch {
    throw new EvoPreviewError('malformed-script-source');
  }

  if (!legacyTagSrc) {
    throw new EvoPreviewError('missing-script-source');
  }

  try {
    const legacyUrl = new URL(legacyTagSrc);
    if (legacyUrl.protocol !== 'http:' && legacyUrl.protocol !== 'https:') {
      throw new EvoPreviewError('malformed-script-source');
    }

    return new URL('/static/js/evo-ibe-tag.js', legacyTag.src).toString();
  } catch (error) {
    if (error instanceof EvoPreviewError) throw error;
    throw new EvoPreviewError('malformed-script-source');
  }
}

export function loadEvoPreview(
  legacyTag: HTMLScriptElement,
  onLateReady?: () => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    let script: HTMLScriptElement;
    let timeoutId: number;
    let graceTimeoutId: number | undefined;
    let settled = false;
    let lateReadyListening = false;

    const cleanupInitialListeners = () => {
      window.removeEventListener(READY_EVENT, handleReady);
      window.removeEventListener(ERROR_EVENT, handleLifecycleError);
      script.removeEventListener('error', handleScriptError);
      window.clearTimeout(timeoutId);
    };
    const cleanupLateReady = () => {
      if (!lateReadyListening) return;
      lateReadyListening = false;
      window.removeEventListener(READY_EVENT, handleLateReady);
      if (graceTimeoutId !== undefined) {
        window.clearTimeout(graceTimeoutId);
        graceTimeoutId = undefined;
      }
    };
    const handleLateReady = () => {
      cleanupLateReady();
      onLateReady?.();
    };
    const startLateReadyReconciliation = () => {
      lateReadyListening = true;
      window.addEventListener(READY_EVENT, handleLateReady);
      graceTimeoutId = window.setTimeout(cleanupLateReady, EVO_PREVIEW_LATE_READY_GRACE_MS);
    };
    const settle = (error?: EvoPreviewError) => {
      if (settled) return;
      settled = true;
      cleanupInitialListeners();
      if (error?.code === 'timeout') startLateReadyReconciliation();
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };
    const handleReady = () => settle();
    const handleLifecycleError = () => settle(new EvoPreviewError('lifecycle-error'));
    const handleScriptError = () => settle(new EvoPreviewError('script-load-error'));

    let scriptUrl: string;
    try {
      scriptUrl = getEvoPreviewScriptUrl(legacyTag);
    } catch (error) {
      reject(error);
      return;
    }

    script = document.createElement('script');
    script.id = EVO_PREVIEW_SCRIPT_ID;
    script.setAttribute(EVO_PREVIEW_SCRIPT_ATTRIBUTE, 'true');
    script.async = true;
    script.src = scriptUrl;
    if (legacyTag.nonce) script.nonce = legacyTag.nonce;

    window.addEventListener(READY_EVENT, handleReady);
    window.addEventListener(ERROR_EVENT, handleLifecycleError);
    script.addEventListener('error', handleScriptError);
    timeoutId = window.setTimeout(
      () => settle(new EvoPreviewError('timeout')),
      EVO_PREVIEW_TIMEOUT_MS
    );

    try {
      const scriptParent = document.head || document.documentElement || document.body;
      if (!scriptParent) throw new EvoPreviewError('script-append-error');
      scriptParent.appendChild(script);
    } catch (error) {
      settle(
        error instanceof EvoPreviewError ? error : new EvoPreviewError('script-append-error')
      );
    }
  });
}

export function exitEvoPreview(): void {
  const currentUrl = window.location.href;
  const liveUrl = removeQueryParamPreservingEncoding(currentUrl, 'evoIbePreview');
  window.location.assign(liveUrl);
}

export function removeEvoPreviewFailure(): void {
  const notice = document.getElementById(EVO_PREVIEW_SCRIPT_ID + '-error');
  if (!notice) return;

  const dialog = notice as HTMLDialogElement;
  try {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
  } catch {
    notice.remove();
    return;
  }
  notice.remove();
}

export function showEvoPreviewFailure(legacyTag: HTMLScriptElement): void {
  if (document.getElementById(EVO_PREVIEW_SCRIPT_ID + '-error')) return;

  const notice = document.createElement('dialog');
  const noticeId = EVO_PREVIEW_SCRIPT_ID + '-error';
  const titleId = noticeId + '-title';
  const messageId = noticeId + '-message';
  const language = getPreviewLanguage(legacyTag);
  const title = document.createElement('h1');
  const message = document.createElement('p');
  const exitButton = document.createElement('button');

  notice.id = noticeId;
  notice.setAttribute('role', 'alertdialog');
  notice.setAttribute('aria-labelledby', titleId);
  notice.setAttribute('aria-describedby', messageId);
  title.id = titleId;
  title.textContent = evo_preview_title[language];
  message.id = messageId;
  message.textContent = evo_preview_error[language];
  exitButton.type = 'button';
  exitButton.textContent = exit_preview[language];
  exitButton.addEventListener('click', exitEvoPreview);
  notice.addEventListener('cancel', event => {
    event.preventDefault();
    exitButton.focus();
  });
  notice.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    exitButton.focus();
  });

  notice.appendChild(title);
  notice.appendChild(message);
  notice.appendChild(exitButton);
  (document.body || document.documentElement).appendChild(notice);

  let isModal = false;
  try {
    if (typeof notice.showModal === 'function') {
      notice.showModal();
      isModal = true;
    } else {
      notice.setAttribute('open', '');
    }
  } catch {
    notice.setAttribute('open', '');
  }
  if (isModal) notice.setAttribute('aria-modal', 'true');

  exitButton.focus();
}
